import React, { useState, useEffect } from 'react';
import aetherisWordmark from './assets/aetheris_wordmark.png';

// 2026-08-29 owner request: MUSIC moved under TV, COMICS moved under GAMES (Wii Games).
const BASE_NAV_ITEMS = [
  { key: 'search',        icon: '⌕',  label: 'SEARCH' },
  { key: 'dashboard',     icon: '⌂',  label: 'HOME' },
  { key: 'movies-browse', icon: '🎬', label: 'MOVIES' },
  { key: 'tv',            icon: '📺', label: 'TV' },
  { key: 'music',         icon: '♪',  label: 'MUSIC' },
  { key: 'anime',         icon: '弐', label: 'ANIME' }, // 2026-08-16: dedicated Anime page
  { key: 'games',         icon: '🎮', label: 'GAMES' },
  { key: 'comics',        icon: '💥', label: 'COMICS' }, // 2026-08-26: dedicated Comic Books page
  { key: 'software',      icon: '💻', label: 'SOFTWARE' },
  { key: 'bbs',           icon: '⌨',  label: 'BBS' },
  { key: 'forum',         icon: '◈',  label: 'FORUM' },
  { key: 'user-vault',    icon: '✦',  label: 'VAULT' },
  { key: 'contact',       icon: '✉',  label: 'CONTACT' },
  { key: 'messages',      icon: '✎',  label: 'MESSAGES' },
];

const ADULT_NAV_ITEM = { key: 'adult', icon: '🔞', label: 'ADULT' };

const COLLAPSED_W = 58;
const EXPANDED_W  = 250;
const MOBILE_BREAK = 768;

const SideNav = ({ currentScreenType, onNavigate, isAdmin, onLogout, showAdult }) => {
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAK);
  // adult_content_enabled is opt-in per user (age-verified once from AccountPage) -- only
  // spliced into the nav when the session says it's on, same placeholder-until-earned
  // pattern already used for the admin-only ADMIN item below.
  const NAV_ITEMS = showAdult ? [...BASE_NAV_ITEMS, ADULT_NAV_ITEM] : BASE_NAV_ITEMS;

  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth <= MOBILE_BREAK);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const nav = (key) => {
    const mapped = key === 'movies' ? 'movies-browse' : key;
    onNavigate && onNavigate(mapped);
  };

  const isActive = (key) =>
    currentScreenType === key ||
    (key === 'movies-browse' && ['movies-browse','library-browse','movie','actor'].includes(currentScreenType));

  /* ── MOBILE: bottom tab bar ── */
  if (isMobile) {
    const allItems = [
      ...NAV_ITEMS,
      ...(isAdmin ? [{ key: 'admin-panel', icon: '⚙', label: 'ADMIN' }] : []),
      { key: '__logout__', icon: '⏻', label: 'OUT' },
    ];
    return (
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 300,
        background: 'rgba(5,4,12,0.98)',
        borderTop: '1px solid rgba(176,38,255,0.18)',
        display: 'flex', flexDirection: 'row',
        overflowX: 'auto', overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        // Base 56px content row + real inset for the iPhone home-indicator strip (only
        // non-zero once viewport-fit=cover is set, see index.html) -- without this the last
        // ~34px of the bar sits under the swipe-up gesture area on an installed PWA.
        height: 'calc(56px + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxSizing: 'border-box',
        backdropFilter: 'blur(12px)',
      }}>
        {allItems.map(item => {
          const active = isActive(item.key);
          const isLogout = item.key === '__logout__';
          const isAdminItem = item.key === 'admin-panel';
          const color = isLogout ? '#ff4466' : isAdminItem ? '#ff8c00' : active ? '#b026ff' : 'rgba(232,224,240,0.5)';
          return (
            <div key={item.key}
              onClick={() => isLogout ? onLogout() : nav(item.key)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: '3px',
                minWidth: '56px', padding: '0 10px',
                cursor: 'pointer', flexShrink: 0,
                borderTop: active ? '2px solid #b026ff' : '2px solid transparent',
                background: active ? 'rgba(176,38,255,0.1)' : 'transparent',
              }}>
              <span style={{ fontSize: '18px', lineHeight: 1, color }}>{item.icon}</span>
              <span style={{ fontSize: '8px', fontWeight: '900', letterSpacing: '0.08em', color, fontFamily: 'Orbitron, monospace', whiteSpace: 'nowrap' }}>{item.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── DESKTOP: collapsible left sidebar ── */
  return (
    <div
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      style={{
        width: expanded ? `${EXPANDED_W}px` : `${COLLAPSED_W}px`,
        minHeight: '100vh',
        position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 300,
        background: 'rgba(5,4,12,0.99)',
        borderRight: '1px solid rgba(176,38,255,0.1)',
        transition: 'width 240ms cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Logo -- 2026-08-28 owner request ("use that [wordmark] somewhere on every page
          where there is space... don't make it obnoxious"): this spot already rendered
          a plain-text "AETHERIS" on every page (the sidenav is persistent chrome, see
          the file-level import comment in App.jsx), so swapping the text for the real
          metallic wordmark (cropped from aetheris-intro-v2.mp4's own logo reveal frame,
          src/assets/aetheris_wordmark.png) covers "every page" in one place instead of
          touching every individual page file. Sized to the same footprint the text
          occupied, collapsed state unchanged (still the small ◈ glyph). */}
      <div style={{
        padding: '18px 0',
        display: 'flex', alignItems: 'center',
        justifyContent: 'flex-start',
        paddingLeft: '16px',
        borderBottom: '1px solid rgba(176,38,255,0.08)',
        marginBottom: '6px', flexShrink: 0, cursor: 'pointer',
        overflow: 'hidden',
      }} onClick={() => nav('dashboard')}>
        <img src={aetherisWordmark} alt="AETHERIS" style={{
          height: '22px',
          width: 'auto',
          opacity: expanded ? 1 : 0,
          transition: 'opacity 180ms',
          filter: 'drop-shadow(0 0 4px rgba(176,38,255,0.35))',
        }} />
        {!expanded && (
          <span style={{ color: '#b026ff', fontSize: '16px', position: 'absolute', left: '18px' }}>◈</span>
        )}
      </div>

      {/* Page-navigation row (2026-08-27 request): was a floating top-center bar in
          App.jsx, independent of the sidenav -- owner asked for it to live IN the sidenav
          instead, so it hides/shows with the same collapse/hover the rest of the rail
          already has rather than always floating over page content. Same four actions
          (home/back/forward/refresh), same collapsed-icon/expanded-label pattern as every
          row below; a home action already exists as its own nav item further down, this
          row keeps it too for parity with the original bar. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', padding: '4px 6px 8px', borderBottom: '1px solid rgba(176,38,255,0.08)', marginBottom: '6px', flexShrink: 0 }}>
        {[
          { icon: '⌂', label: 'HOME', action: () => nav('dashboard') },
          { icon: '←', label: 'BACK', action: () => window.history.back() },
          { icon: '→', label: 'FORWARD', action: () => window.history.forward() },
          { icon: '↺', label: 'REFRESH', action: () => window.location.reload() },
        ].map(({ icon, label, action }) => (
          <div key={label} onClick={action} title={label}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
              transition: 'background 150ms', whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(176,38,255,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
            <span style={{ fontSize: '15px', width: '20px', textAlign: 'center', flexShrink: 0, color: 'rgba(176,38,255,0.6)' }}>{icon}</span>
            <span style={{
              fontSize: '10px', fontWeight: '700', letterSpacing: '0.08em',
              color: 'rgba(176,38,255,0.6)', fontFamily: 'Orbitron, monospace',
              opacity: expanded ? 1 : 0, transition: 'opacity 180ms',
            }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Nav items */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', padding: '4px 6px', overflowY: 'auto', overflowX: 'hidden' }}>
        {NAV_ITEMS.map(item => {
          const active = isActive(item.key);
          return (
            <div key={item.key} onClick={() => nav(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '11px 10px', borderRadius: '8px',
                cursor: 'pointer',
                background: active ? 'rgba(176,38,255,0.15)' : 'transparent',
                borderLeft: active ? '4px solid #b026ff' : '4px solid transparent',
                transition: 'background 150ms', whiteSpace: 'nowrap',
                overflow: 'hidden', flexShrink: 0,
              }}
              onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(176,38,255,0.05)'; }}
              onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: '17px', flexShrink: 0, width: '22px', textAlign: 'center', lineHeight: 1, color: active ? '#b026ff' : 'rgba(232,224,240,0.65)', transition: 'color 150ms' }}>{item.icon}</span>
              <span style={{
                fontSize: '10px', fontWeight: '900', letterSpacing: '0.12em',
                color: active ? '#b026ff' : 'rgba(232,224,240,0.65)',
                opacity: expanded ? 1 : 0,
                transition: 'opacity 180ms',
                whiteSpace: 'nowrap',
              }}>{item.label}</span>
            </div>
          );
        })}
      </div>

      {/* Bottom */}
      <div style={{ padding: '6px', borderTop: '1px solid rgba(176,38,255,0.08)', flexShrink: 0 }}>
        {isAdmin && (
          <div onClick={() => nav('admin-panel')}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 10px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', transition: 'background 150ms' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,140,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <span style={{ fontSize: '10px', flexShrink: 0, width: '22px', textAlign: 'center', fontWeight: '900', color: '#ff8c00' }}>⚙</span>
            <span style={{ fontSize: '10px', fontWeight: '900', color: '#ff8c00', opacity: expanded ? 1 : 0, transition: 'opacity 180ms', whiteSpace: 'nowrap' }}>ADMIN</span>
          </div>
        )}
        <div onClick={onLogout}
          style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 10px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', transition: 'background 150ms' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,68,102,0.08)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span style={{ fontSize: '10px', flexShrink: 0, width: '22px', textAlign: 'center', fontWeight: '900', color: '#ff4466' }}>⏻</span>
          <span style={{ fontSize: '10px', fontWeight: '900', color: '#ff4466', opacity: expanded ? 1 : 0, transition: 'opacity 180ms', whiteSpace: 'nowrap' }}>LOG OUT</span>
        </div>
      </div>
    </div>
  );
};

export default SideNav;
